/// HOOKS ///
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
import { useEffect } from "react";
import { useState } from "react";
/// ACTIONS ///
import { updateLocalCounter } from "api/actions/sketch/counter";
import { restore } from "api/actions/history";
import { replaceRestorePoint } from "api/actions/history";
/// TYPES ///
import { Counter } from "api/types/sketch";
import { Corner } from "api/types/sketch";
import { IHistoryActionType } from "api/types/history";
import { Slab } from "api/types/quote";
import { CornerError } from "api/types/quote/error";
/// COMPONENTS ///
import UpdateCorner from "components/quote/drawer/update_corner";
/// FUNCTIONS ///
import { getFirstSlabInAreas } from "functions/sketch";
import { updateCorner } from "functions/sketch";
import { handleUpdateCounter } from "handler/counter/update";
/// VALUES ///
import { STANDARD } from "values/quote";

interface Props {
  open: boolean,
  setOpen: (open: boolean) => void,
  original_corner: Corner | null,
  selected_counter: Counter | null
}

export default function UpdateStoreCorner({ open, setOpen, original_corner, selected_counter }: Props) {
  const dispatch = useAppDispatch();
  const { areas } = useAppSelector(state => state.quote);
  const { cut_shapes } = useAppSelector(state => state.sketch);
  const [error, setError] = useState<CornerError>({});
  const [corner, setCorner] = useState<Corner>({});

  useEffect(() => {
    if (open && original_corner) {
      setCorner(original_corner);
      dispatch(replaceRestorePoint(
        {
          undo_action: IHistoryActionType.UPDATE_COUNTER,
          desc: "Cancel Update Corner",
          undo_data: { ...selected_counter }
        }
      ));
    }
  }, [open, dispatch]);

  const save = () => {
    if (selected_counter) {
      const counter: Counter = updateCorner(selected_counter, corner);
      const slab: Slab | null = getFirstSlabInAreas(areas, counter.area_uuid);
      handleUpdateCounter(
        dispatch,
        { ...selected_counter },
        counter,
        cut_shapes.filter(shape => shape.counter_uuid === selected_counter.uuid),
        slab.uuid,
        `Change ${corner.corner_type.toUpperCase()} Corner`
      );
      setError({});
      setOpen(false);
    }
  }

  const update = (corner: Corner) => {
    const counter: Counter = updateCorner(selected_counter, corner);
    dispatch(updateLocalCounter(counter));
    setCorner(corner);
  }

  const cancel = () => {
    dispatch(restore());
    setError({});
    setOpen(false);
  }

  return (
    <UpdateCorner
      open={open}
      setOpen={setOpen}
      cancel={cancel}
      save={save}
      corner_type={corner?.corner_type ?? STANDARD}
      setCornerType={corner_type => update({ ...corner, corner_type })}
      corner_radius={corner?.corner_radius ?? 0}
      setCornerRadius={corner_radius => update({ ...corner, corner_radius })}
      radius_error={error?.radius_error}
      radius_has_error={error?.radius_has_error}
      corner_length={corner?.corner_length ?? 0}
      setCornerLength={corner_length => update({ ...corner, corner_length })}
      length_error={error?.length_error}
      length_has_error={error?.length_has_error}
      corner_depth={corner?.corner_depth ?? 0}
      setCornerDepth={corner_depth => update({ ...corner, corner_depth })}
      depth_error={error?.depth_error}
      depth_has_error={error?.depth_has_error}
    />
  );
}
